import _SessionService from 'ember-simple-auth/services/session';
import mockWindow from 'ember-window-mock';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import { removeAccessCookie } from 'cio/utils/auth-cookies';
import HydraService from './hydra';
import {
  clearAttemptedTransitionUrl,
  getAttemptedTransitionUrl,
  setAttemptedTransitionUrl,
} from '@customerio/hydra-nav-state';

// We import this file in session.js, to avoid namespace collisions
// between our .ts service definitions and .js definitions from services from
// addons.
// This is to avoid the following warning:
//
// WARNING: Detected collisions between .js and .ts files of the same name.
// This can result in nondeterministic build output; see https://git.io/JvIwo for more information.

export default class SessionService extends _SessionService {
  @service router!: RouterService;
  @service hydra!: HydraService;

  loggingOutByExplicitUserAction = false;
  lastVisitedRoute = '';
  redirectToAfterLogin = '';

  handleInvalidation(_routeAfterInvalidation?: string) {
    removeAccessCookie();
    ['cio-features', 'cio-settings'].forEach((k) => window.localStorage.removeItem(k));

    // originally this method receives "routeAfterInvalidation" hard-coded in app onfiguration
    // we ignore it to add conditional "?loggedOut=1" query param when logout happened by explicit user action (clicking a "logout" button) - required for displaying logged-out success alert
    //
    // we use a session instance property instead of function param for simplicity: to avoid overriding multiple methods across the call stack:
    let redirectPath = '/login';
    if (this.loggingOutByExplicitUserAction) {
      redirectPath = '/login?loggedOut=1';
    } else if (this.lastVisitedRoute) {
      setAttemptedTransitionUrl(this.lastVisitedRoute);
      // If a logout happens programatically we store the origin page so they can be redirected back to it after login
      redirectPath = `/login?origin=${encodeURIComponent(this.lastVisitedRoute)}`;
    }

    mockWindow.location.replace(redirectPath);
  }

  // In context of Customer.io application, authenticator call can happen
  // already within context of authenticated route. This override ensures that
  // in such scenarios, user is not redirected to index application route
  handleAuthentication(_routeAfterInvalidation?: string) {
    if (this.router.isActive('authenticated')) {
      return;
    }

    // If a user is authenticated from the signup route, it's in the signup flow
    // and thus should be redirected to the post registration route.
    if (this.router.isActive('signup')) {
      let { signupType } = this.router.currentRoute.queryParams;
      return this.router.transitionTo('authenticated.post_registration', { queryParams: { signupType } });
    }

    // Reload the application after logging in: removes any tracking scripts
    // injected to the logout pages, etc.

    // Try to redirect to attmptedTransition, which is the requested route before being redirected to login
    let attemptedUrl = (this.attemptedTransition as any)?.intent?.url;

    // If the messaging UI is embedded in Hydra and a transition URL entry
    // exists, reload the page to that URL, to prevent the marketing UI from
    // rendering without the navigation sidebar.
    // Once login is handled fully by Hydra, this will be removed
    if (this.hydra.isHydraEnabled) {
      let transitionUrl = getAttemptedTransitionUrl();
      if (transitionUrl != null) {
        attemptedUrl = transitionUrl;
        clearAttemptedTransitionUrl();
      }
    }

    if (attemptedUrl != null) {
      mockWindow.location.href = attemptedUrl;
    }

    if (this.redirectToAfterLogin) {
      try {
        let decodedOrigin = decodeURIComponent(this.redirectToAfterLogin);
        mockWindow.location.replace(decodedOrigin);
      } catch (e) {} //not doing anything to handle this error, just want to avoid the app breaking
    } else {
      mockWindow.location.reload();
    }
    return super.handleAuthentication('authenticated');
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    session: SessionService;
  }
}
